"use client";

/**
 * Shared pool of artist-content thumbnails — release covers, artist photos +
 * gallery images/GIFs, track cover art. One fetch per session (module cache),
 * mirrored into localStorage so even a cold-boot splash can show a thumbnail
 * synchronously. Consumers: the top-left logo slideshow, the loading screen
 * (random thumb instead of the BB logo) and the visitor header background.
 */

let pool: string[] | null = null;
let poolPromise: Promise<string[]> | null = null;
/** What each thumbnail is — title + page — so the loading screen can name and
 *  link the artwork that is spinning (Gordon 2026-10-30). */
export interface ThumbMeta { title: string; href: string }
let meta: Record<string, ThumbMeta> = {};

const LS_KEY = "bb-content-thumb-pool";
const LS_META_KEY = "bb-content-thumb-meta";

/** Title + page for a pool thumbnail, if known. */
export function getThumbMeta(url: string | null | undefined): ThumbMeta | null {
  if (!url) return null;
  if (!meta[url]) {
    try { meta = { ...JSON.parse(localStorage.getItem(LS_META_KEY) || "{}"), ...meta }; } catch { /* ignore */ }
  }
  return meta[url] || null;
}

/** Synchronous best-effort read — module cache, then the localStorage
 *  snapshot from a previous session. Returns [] when neither exists yet. */
export function getContentThumbsSync(): string[] {
  if (pool && pool.length) return pool;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length) return arr.filter((u: any) => typeof u === "string");
    }
  } catch { /* ignore */ }
  return [];
}

/** One random thumbnail, or null when nothing is cached yet. */
export function getRandomContentThumbSync(): string | null {
  const arr = getContentThumbsSync();
  return arr.length ? arr[Math.floor(Math.random() * arr.length)] : null;
}

export interface ContentThumbSources {
  releases?: boolean;
  artists?: boolean;
  artistGallery?: boolean;
  tracks?: boolean;
}

export async function loadContentThumbs(sources?: ContentThumbSources): Promise<string[]> {
  if (pool && pool.length) return pool;
  if (poolPromise) return poolPromise;
  const s = { releases: true, artists: true, artistGallery: true, tracks: true, ...(sources || {}) };
  poolPromise = (async () => {
    const thumbs = new Set<string>();
    const add = (u: unknown, title: unknown, href: string) => {
      if (typeof u !== "string" || !u.startsWith("http")) return;
      thumbs.add(u);
      if (typeof title === "string" && title && !meta[u]) meta[u] = { title, href };
    };
    try {
      const { collection, getDocs, query, limit } = await import("firebase/firestore");
      const { db } = await import("@/lib/firebase");
      await Promise.allSettled([
        (async () => {
          if (s.releases !== false) try {
            const rel = await getDocs(query(collection(db, "releases"), limit(80)));
            rel.forEach(d => {
              const data = d.data() as any;
              if (data.adminHidden || data.hidden) return;
              add(data.coverArtUrl, data.title, `/music/${d.id}`);
            });
          } catch { /* public read — non-fatal */ }
        })(),
        (async () => {
          if (s.artists !== false || s.artistGallery !== false) try {
            const art = await getDocs(query(collection(db, "artists"), limit(50)));
            art.forEach(d => {
              const data = d.data() as any;
              if (data.adminHidden) return;
              const artistHref = `/artists/${data.slug || d.id}`;
              if (s.artists !== false) add(data.imageUrl, data.name, artistHref);
              if (s.artistGallery !== false && Array.isArray(data.images)) {
                data.images.slice(0, 4).forEach((g: any) => add(g, data.name, artistHref));
              }
            });
          } catch { /* non-fatal */ }
        })(),
        (async () => {
          if (s.tracks !== false) try {
            const wk = await getDocs(query(collection(db, "works"), limit(80)));
            wk.forEach(d => {
              const data = d.data() as any;
              if (data.adminHidden || data.hidden || data.privacy === "private" || data.privacy === "unlisted") return;
              add(data.coverArtUrl || data.imageUrl, data.title, `/music/${d.id}`);
            });
          } catch { /* non-fatal */ }
        })()
      ]);
    } catch { /* firebase not ready — leave pool empty */ }

    const arr = [...thumbs];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    pool = arr.slice(0, 60);
    try {
      if (pool.length) {
        localStorage.setItem(LS_KEY, JSON.stringify(pool));
        const kept: Record<string, ThumbMeta> = {};
        pool.forEach(u => { if (meta[u]) kept[u] = meta[u]; });
        localStorage.setItem(LS_META_KEY, JSON.stringify(kept));
      }
    } catch { /* quota */ }
    return pool;
  })().finally(() => { poolPromise = null; });
  return poolPromise;
}
